import React, { useState } from 'react';
import { ILegalClientFullObject } from '../../Interfaces/ILegalClient';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { academicTitles, genders } from '../../constants';
import { IGender } from '../../Interfaces/IGender';
import {
  ILegalClientAddress,
  initAddress,
} from '../../Interfaces/ILegalClientAddress';
import {
  ILegalClientContactInformation,
  initContactInformation,
} from '../../Interfaces/ILegalClientContactInformation';
import { PostcodeSearchV3 } from './PostcodeSearchV3';
import { IPostcode } from '../../Interfaces/IPostcode';
import { IConfig } from '../../Interfaces/IConfig';

interface IProps {
  configObject: IConfig;
  legalClient: ILegalClientFullObject;
  setLegalClient: Function;
}

export const ClientInformationLegalClient: React.FC<IProps> = ({
  configObject,
  legalClient,
  setLegalClient,
}) => {
  const [wasSelect, setWasSelect] = useState(false);
  const [hasContact, setHasContact] = useState(false);

  //
  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
    useState(false);
  const [isDsgvoOpen, setIsDsgvoOpen] = useState(false);

  const handleDsgvoOpen = () => {
    setIsDsgvoOpen(true);
  };

  const handleDsgvoClose = () => {
    setIsDsgvoOpen(false);
  };
  //
  const address =
    legalClient.AddressArray.length !== 1
      ? initAddress()
      : legalClient.AddressArray[0];
  const setAddress = (updatedAddress: ILegalClientAddress) => {
    setLegalClient({
      ...legalClient,
      AddressArray: [updatedAddress],
    });
  };
  //
  const phoneContact =
    legalClient.ContactInformationArray.length !== 1
      ? initContactInformation()
      : legalClient.ContactInformationArray[0];
  const setPhoneContact = (updatedContact: ILegalClientContactInformation) => {
    setLegalClient({
      ...legalClient,
      ContactInformationArray: [updatedContact],
    });
  };
  //
  const postcodeObject: IPostcode | undefined =
    address.idPostcode > 0
      ? ({
          idPostcode: address.idPostcode,
          Postcode: address.Postcode,
          City: address.City,
          idCountry: address.idCountry,
        } as IPostcode)
      : undefined;

  if (!wasSelect) {
    return (
      <>
        <Typography variant='h6' sx={{ mb: 3 }}>
          Ihre Kontaktinformationen
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Card
              variant='elevation'
              sx={{
                height: 100,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                setHasContact(false);
                setWasSelect(true);
              }}
            >
              <CardContent sx={{ textAlign: 'center', padding: 0 }}>
                Neues Konto anlegen
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card
              variant='elevation'
              sx={{
                height: 100,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                setHasContact(true);
                setWasSelect(true);
              }}
            >
              <CardContent sx={{ textAlign: 'center', padding: 0 }}>
                Bestehendes Konto verwenden
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Dialog
        open={isDsgvoOpen}
        onClose={handleDsgvoClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {'Datenschutzerklärung'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <div dangerouslySetInnerHTML={{ __html: configObject.gdpr_html }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDsgvoClose}>Zurück</Button>
        </DialogActions>
      </Dialog>

      <Typography variant='h6' sx={{ mb: 3 }}>
        Ihre Kontaktinformationen
      </Typography>
      {hasContact ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              value={legalClient.Email || ''}
              label='E-Mail'
              size='small'
              type='email'
              fullWidth
              onChange={event => {
                setLegalClient({
                  ...legalClient,
                  Email: event.target.value,
                });
              }}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <TextField
              select
              label='Akademischer Titel'
              size='small'
              fullWidth
              value={legalClient.idAcademicTitle || ''}
              onChange={event => {
                const academicTitleId = Number(event.target.value);
                setLegalClient({
                  ...legalClient,
                  idAcademicTitle: academicTitleId,
                  AcademicTitle: academicTitles.find(
                    title => title.idAcademicTitle === academicTitleId
                  )?.AcademicTitle,
                });
              }}
            >
              {academicTitles.map((title, i) => (
                <MenuItem key={i} value={title.idAcademicTitle}>
                  {title.AcademicTitle}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={5}>
            <TextField
              required
              value={legalClient.FirstName || ''}
              label='Vorname'
              size='small'
              fullWidth
              onChange={event => {
                setLegalClient({
                  ...legalClient,
                  FirstName: event.target.value,
                });
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              required
              value={legalClient.LastName || ''}
              label='Nachname'
              size='small'
              fullWidth
              onChange={event => {
                setLegalClient({
                  ...legalClient,
                  LastName: event.target.value,
                });
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              required
              value={legalClient.idGender || ''}
              label='Geschlecht'
              size='small'
              fullWidth
              onChange={event => {
                const genderId = Number(event.target.value);

                setLegalClient({
                  ...legalClient,
                  idGender: genderId,
                  Gender: genders.find(gender => gender.idGender === genderId)
                    ?.Gender,
                });
              }}
            >
              {genders.map((gender: IGender, i) => (
                <MenuItem key={i} value={gender.idGender}>
                  {gender.Gender}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              value={legalClient.Email || ''}
              label='E-Mail'
              size='small'
              type='email'
              fullWidth
              onChange={event => {
                setLegalClient({
                  ...legalClient,
                  Email: event.target.value,
                });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              value={phoneContact.Contact || ''}
              label='Telefonnummer'
              size='small'
              fullWidth
              onChange={event => {
                setPhoneContact({
                  ...phoneContact,
                  Contact: event.target.value,
                });
              }}
            />
          </Grid>

          <Grid item xs={8}>
            <TextField
              required
              value={address.Street || ''}
              label='Straße'
              size='small'
              fullWidth
              onChange={event => {
                setAddress({
                  ...address,
                  Street: event.target.value,
                });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              required
              value={address.Number || ''}
              label='Hausnummer'
              size='small'
              fullWidth
              onChange={event => {
                setAddress({
                  ...address,
                  Number: event.target.value,
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <PostcodeSearchV3
              postcodeObject={postcodeObject}
              required={true}
              updatePostcodeObject={value => {
                if (!value) return;

                setAddress({
                  ...address,
                  idPostcode: value.idPostcode!,
                  Postcode: value.Postcode,
                  City: value.City,
                  idCountry: value.idCountry,
                  Country: 'Deutschland',
                });
              }}
            />
          </Grid>
        </Grid>
      )}
      <Box sx={{ mt: 5 }} />
      <Alert severity='info' icon={false}>
        <FormControlLabel
          label={
            <Typography component='span'>
              Ich bin mit der Verarbeitung meiner Daten zur Interaktion und zum
              Datenaustausch mit dem Mandantenportal einverstanden. Die&nbsp;
              <Typography
                component='span'
                onClick={handleDsgvoOpen}
                sx={{
                  color: 'blue',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Datenschutzerklärung
              </Typography>
              &nbsp; habe ich zur Kenntnis genommen.
            </Typography>
          }
          control={
            <Checkbox
              checked={isTermsAndConditionsChecked}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setIsTermsAndConditionsChecked(event.target.checked)
              }
              inputProps={{ 'aria-label': 'controlled' }}
              required
            />
          }
        />
      </Alert>
    </>
  );
};
